import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ElementRef, HostListener } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { map, take, filter, takeUntil } from "rxjs/operators";
import { AuthService } from "../../services/auth.service";
import { ModalService } from "../../services/modal.service";
import { TruthManagement } from "../../services/truth-management";
import { PageHeaderComponent } from '../page-header/page-header.component';
import { StatsService } from "../../services/stats-dashboard.service";
import {
  FilteredStats,
  AvailableFilters,
  StatsInsights,
  StatsTableRow,
  StatsFilters,
  AccuracyRangeOption,
  ModelStatusCounts,
  EvaluationOutcome,
} from "../../models/stats-dashboard.model";

@Component({
  selector: "app-stats-dashboard",
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent],
  providers: [StatsService], // Component-scoped
  templateUrl: "./stats-dashboard.component.html",
  styleUrls: ["./stats-dashboard.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatsDashboardComponent implements OnInit, OnDestroy {
  // Observables
  filteredStats$!: Observable<FilteredStats>;
  availableFilters$!: Observable<AvailableFilters>;
  insights$!: Observable<StatsInsights>;
  
  // New observables for redesigned dashboard
  modelStatusCounts$!: Observable<ModelStatusCounts>;
  evaluationOutcome$!: Observable<EvaluationOutcome>;
  hasModelSelected$!: Observable<boolean>;
  hasActiveFilters$!: Observable<boolean>;
  isDataLoaded$!: Observable<boolean>;

  // Table data
  tableRows$!: Observable<StatsTableRow[]>;

  // Sorting state
  sortColumn: 'model' | 'field' | 'accuracy' = 'accuracy';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Excel-style filter dropdowns (only one can be active at a time)
  isModelsDropdownOpen: boolean = false;
  isAccuracyDropdownOpen: boolean = false;
  
  // Model filter search
  modelSearchText: string = '';
  
  // Collapse state for insights sections (start collapsed)
  isTopFilesCollapsed: boolean = true;
  isLeastFilesCollapsed: boolean = true;
  isLeastFieldsCollapsed: boolean = true;

  // Authentication
  isAuthenticated = false;

  private destroy$ = new Subject<void>();

  constructor(
    private statsService: StatsService,
    public router: Router,
    private elementRef: ElementRef,
    public authService: AuthService,
    private modalService: ModalService,
    private truthManagementService: TruthManagement,
  ) {}

  ngOnInit(): void {
    this.filteredStats$ = this.statsService.filteredStats$;
    this.availableFilters$ = this.statsService.availableFilters$;
    this.insights$ = this.statsService.insights$;
    
    // New observables for redesigned dashboard
    this.modelStatusCounts$ = this.statsService.modelStatusCounts$;
    this.evaluationOutcome$ = this.statsService.evaluationOutcome$;
    
    // Track if any model is selected
    this.hasModelSelected$ = this.statsService.filters$.pipe(
      map(filters => filters.selectedModels.length > 0)
    );

    // Show Reset All only when user has explicitly applied a filter
    this.hasActiveFilters$ = this.statsService.filters$.pipe(
      map(filters => filters.selectedModels.length > 0 || filters.accuracyRanges.length > 0)
    );

    // True once the real HTTP response has arrived
    this.isDataLoaded$ = this.statsService.isDataLoaded$;

    // Transform fields to table rows with sorting
    this.tableRows$ = this.filteredStats$.pipe(
      map((stats) => {
        const rows = stats.fields.map((field) => ({
          model: field.model,
          field: field.fieldName,
          accuracy: field.accuracy,
        }));
        
        // Sort by selected column
        return rows.sort((a, b) => {
          const aVal = a[this.sortColumn];
          const bVal = b[this.sortColumn];
          const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          return this.sortDirection === 'asc' ? comparison : -comparison;
        });
      }),
    );
    
    // Initialize authentication and user role
    this.isAuthenticated = this.authService.hasValidToken();

    // Note: Do NOT subscribe to currentClient$ here - StatsService already listens and handles
    // client changes automatically. Subscribing here causes double API calls and breaks caching.

    // Reload stats data when an evaluation completes
    this.truthManagementService.evaluationCompleted$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.statsService.reload());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== AUTHENTICATION & USER METHODS ====================

  /**
   * Get current user email from sessionStorage
   */
  logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  /**
   * Navigate to admin settings page
   */
  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.router.navigate(['/indexing']);
  }

  navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  // ==================== FILTER ACTIONS ====================

  toggleModel(model: string): void {
    const currentFilters = this.statsService.getCurrentFilters();
    const selectedModels = currentFilters.selectedModels;

    let newSelectedModels: string[];
    if (selectedModels.includes(model)) {
      newSelectedModels = selectedModels.filter((m) => m !== model);
    } else {
      newSelectedModels = [...selectedModels, model];
    }

    // Auto-clear accuracy filter when manually selecting models
    this.statsService.updateFilters({
      selectedModels: newSelectedModels,
      accuracyRanges: []
    });
  }

  toggleFile(file: string): void {
    const currentFilters = this.statsService.getCurrentFilters();
    const selectedFiles = currentFilters.selectedFiles;

    if (selectedFiles.includes(file)) {
      this.statsService.updateFilters({
        selectedFiles: selectedFiles.filter((f) => f !== file),
      });
    } else {
      this.statsService.updateFilters({
        selectedFiles: [...selectedFiles, file],
      });
    }
  }

  toggleField(field: string): void {
    const currentFilters = this.statsService.getCurrentFilters();
    const selectedFields = currentFilters.selectedFields;

    if (selectedFields.includes(field)) {
      this.statsService.updateFilters({
        selectedFields: selectedFields.filter((f) => f !== field),
      });
    } else {
      this.statsService.updateFilters({
        selectedFields: [...selectedFields, field],
      });
    }
  }

  resetFilters(): void {
    this.statsService.resetFilters();
  }

  // ==================== EXCEL-STYLE FILTER METHODS ====================
  
  /**
   * Toggle model dropdown - auto-closes accuracy dropdown
   */
  toggleModelsDropdown(): void {
    this.isModelsDropdownOpen = !this.isModelsDropdownOpen;
    if (this.isModelsDropdownOpen) {
      this.isAccuracyDropdownOpen = false; // Close other dropdown
    }
  }

  /**
   * Toggle accuracy dropdown - auto-closes models dropdown
   */
  toggleAccuracyDropdown(): void {
    this.isAccuracyDropdownOpen = !this.isAccuracyDropdownOpen;
    if (this.isAccuracyDropdownOpen) {
      this.isModelsDropdownOpen = false; // Close other dropdown
    }
  }

  /**
   * Close all dropdowns (e.g., when clicking outside)
   */
  closeAllDropdowns(): void {
    this.isModelsDropdownOpen = false;
    this.isAccuracyDropdownOpen = false;
  }

  /**
   * Handle click outside dropdowns to close them
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedElement = event.target as HTMLElement;
    const isClickInside = this.elementRef.nativeElement.contains(clickedElement);
    
    // Close dropdowns only if click is outside the component's filter dropdowns
    if (!isClickInside || (!clickedElement.closest('.filter-dropdown') && !clickedElement.closest('.filter-dropdown-panel'))) {
      this.closeAllDropdowns();
    }
  }

  /**
   * Clear model filter - removes all selected models
   */
  clearModelFilter(): void {
    this.statsService.updateFilters({ selectedModels: [] });
    this.modelSearchText = '';
  }

  /**
   * Clear accuracy filter and auto-clear model filter
   */
  clearAccuracyFilter(): void {
    this.statsService.updateFilters({ accuracyRanges: [] });
    // When accuracy is cleared, reset to all models
    this.clearModelFilter();
  }

  /**
   * Select all filtered models
   */
  selectAllModels(): void {
    const filtered = this.getFilteredModels();
    this.statsService.updateFilters({ 
      selectedModels: filtered,
      accuracyRanges: [] // Auto-clear accuracy filter
    });
  }

  /**
   * Deselect all models
   */
  deselectAllModels(): void {
    this.statsService.updateFilters({ 
      selectedModels: [],
      accuracyRanges: [] // Auto-clear accuracy filter
    });
    this.modelSearchText = '';
  }

  /**
   * Get filtered models based on search text
   */
  getFilteredModels(): string[] {
    const allModels = this.availableFilters$ ? [] : []; // Will be filled from observable
    let models: string[] = [];
    this.availableFilters$.pipe(take(1)).subscribe(filters => {
      models = filters.models;
    });
    
    if (!this.modelSearchText.trim()) {
      return models;
    }
    
    const searchLower = this.modelSearchText.toLowerCase();
    return models.filter(m => m.toLowerCase().includes(searchLower));
  }

  /**
   * Get count of selected models
   */
  getSelectedModelsCount(): number {
    return this.getCurrentFilters().selectedModels.length;
  }

  /**
   * Get count of selected accuracy ranges
   */
  getSelectedAccuracyCount(): number {
    return this.getCurrentFilters().accuracyRanges.length;
  }

  isModelSelected(model: string): boolean {
    return this.statsService.getCurrentFilters().selectedModels.includes(model);
  }

  isFileSelected(file: string): boolean {
    return this.statsService.getCurrentFilters().selectedFiles.includes(file);
  }

  isFieldSelected(field: string): boolean {
    return this.statsService.getCurrentFilters().selectedFields.includes(field);
  }

  toggleAccuracyRange(range: AccuracyRangeOption): void {
    const currentRanges = this.getCurrentFilters().accuracyRanges;
    const index = currentRanges.indexOf(range);
    
    let newRanges: AccuracyRangeOption[];
    if (index > -1) {
      // Remove range
      newRanges = currentRanges.filter(r => r !== range);
    } else {
      // Add range
      newRanges = [...currentRanges, range];
    }
    
    // Auto-select models based on the accuracy ranges
    this.statsService.rawStats$.pipe(take(1)).subscribe(stats => {
      let modelsInRanges: string[] = [];
      
      if (newRanges.length > 0) {
        // Get all models that match ANY of the selected ranges
        modelsInRanges = stats.models.filter(model => {
          return newRanges.some(r => {
            switch (r) {
              case '0-40':
                return model.averageAccuracy >= 0 && model.averageAccuracy < 40;
              case '40-70':
                return model.averageAccuracy >= 40 && model.averageAccuracy < 70;
              case '70-80':
                return model.averageAccuracy >= 70 && model.averageAccuracy < 80;
              case '80-90':
                return model.averageAccuracy >= 80 && model.averageAccuracy < 90;
              case '90-100':
                return model.averageAccuracy >= 90 && model.averageAccuracy <= 100;
              default:
                return false;
            }
          });
        }).map(model => model.modelName);
      } else {
        // If no ranges selected, select all models
        modelsInRanges = stats.models.map(m => m.modelName);
      }
      
      // Update filters with new ranges and auto-selected models
      this.statsService.updateFilters({ 
        accuracyRanges: newRanges,
        selectedModels: modelsInRanges
      });
    });
  }

  /**
   * Select all accuracy ranges
   */
  selectAllAccuracyRanges(): void {
    const allRanges: AccuracyRangeOption[] = ['0-40', '40-70', '70-80', '80-90', '90-100'];
    this.statsService.rawStats$.pipe(take(1)).subscribe(stats => {
      const allModels = stats.models.map(m => m.modelName);
      this.statsService.updateFilters({ 
        accuracyRanges: allRanges,
        selectedModels: allModels
      });
    });
  }

  /**
   * Deselect all accuracy ranges
   */
  deselectAllAccuracyRanges(): void {
    this.clearAccuracyFilter();
  }

  isAccuracyRangeSelected(range: AccuracyRangeOption): boolean {
    return this.getCurrentFilters().accuracyRanges.includes(range);
  }

  getCurrentFilters(): StatsFilters {
    return this.statsService.getCurrentFilters();
  }

  /**
   * Extract filename from full path
   * Example: "ground_truths/balloon_rider/Balloon Rider3 (1).pdf" => "Balloon Rider3 (1).pdf"
   */
  getFileName(fullPath: string): string {
    if (!fullPath) return '';
    const parts = fullPath.split('/');
    return parts[parts.length - 1];
  }

  // ==================== SORTING ====================

  sortBy(column: 'model' | 'field' | 'accuracy'): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    
    // Re-trigger table rows observable
    this.tableRows$ = this.filteredStats$.pipe(
      map((stats) => {
        const rows = stats.fields.map((field) => ({
          model: field.model,
          field: field.fieldName,
          accuracy: field.accuracy,
        }));
        
        return rows.sort((a, b) => {
          const aVal = a[this.sortColumn];
          const bVal = b[this.sortColumn];
          const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          return this.sortDirection === 'asc' ? comparison : -comparison;
        });
      }),
    );
  }

  getSortIcon(column: 'model' | 'field' | 'accuracy'): string {
    if (this.sortColumn !== column) return '';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  // ==================== NAVIGATION ====================

  /**
   * Navigate to Truth Management (Ground Truth) page for specific sample file
   * Route: /truth-management?documentCode={documentModel}&fileName={fileName}
   */
  navigateToSample(documentModel: string, fileName: string): void {
    this.router.navigate(['/truth-management'], { 
      queryParams: { documentCode: documentModel, fileName: fileName }
    });
  }

  /**
   * Navigate to Truth Management (Ground Truth) page for specific document model
   * When user clicks on a file/sample in the table
   * Route: /truth-management?documentCode={documentModel}
   * Example: /truth-management?documentCode=1-4_family_rider
   */
  navigateToFile(documentModel: string): void {
    this.router.navigate(['/truth-management'], { 
      queryParams: { documentCode: documentModel }
    });
  }

  /**
   * Navigate to Prompt Tuning page with document model as route param
   * When user clicks on a field in the table
   * Prompt Tuning route is /prompt-tuning/:documentModel
   * Field name is passed as query parameter to prefill the field
   */
  navigateToField(documentModel: string, fieldName: string): void {
    this.router.navigate(['/prompt-tuning', documentModel], {
      queryParams: { field: fieldName, source: 'evaluation-prompt-tuning' }
    });
  }

  /**
   * Navigate to Ground Truth page for a file from the filter panel
   * Looks up the model for the file from stats data
   */
  navigateToFilterFile(fileName: string, event: Event): void {
    event.stopPropagation(); // Prevent checkbox toggle
    this.filteredStats$.pipe(take(1)).subscribe(stats => {
      const file = stats.files.find(f => f.fileName === fileName);
      const model = file?.model || this.getCurrentFilters().selectedModels[0] || '';
      if (model) {
        this.router.navigate(['/truth-management'], {
          queryParams: { documentCode: model, fileName: fileName }
        });
      }
    });
  }

  /**
   * Navigate to Prompt Tuning page for a field from the filter panel
   * Uses first selected model or first available model from stats
   */
  navigateToFilterField(fieldName: string, event: Event): void {
    event.stopPropagation(); // Prevent checkbox toggle
    const selectedModels = this.getCurrentFilters().selectedModels;
    if (selectedModels.length > 0) {
      this.router.navigate(['/prompt-tuning', selectedModels[0]], {
        queryParams: { field: fieldName, source: 'evaluation-prompt-tuning' }
      });
    } else {
      // Use first available model from stats
      this.filteredStats$.pipe(take(1)).subscribe(stats => {
        const firstModel = stats.models[0]?.modelName;
        if (firstModel) {
          this.router.navigate(['/prompt-tuning', firstModel], {
            queryParams: { field: fieldName, source: 'evaluation-prompt-tuning' }
          });
        }
      });
    }
  }

  // ==================== ACCURACY COLOR ====================

  getAccuracyClass(accuracy: number): string {
    if (accuracy >= 90) return "accuracy-excellent";
    if (accuracy >= 80) return "accuracy-very-good";
    if (accuracy >= 70) return "accuracy-good";
    if (accuracy >= 40) return "accuracy-fair";
    return "accuracy-poor";
  }

  // ==================== INSIGHTS COLLAPSE/EXPAND ====================

  /**
   * Expand all insights sections
   */
  private expandAllInsights(): void {
    this.isTopFilesCollapsed = false;
    this.isLeastFilesCollapsed = false;
    this.isLeastFieldsCollapsed = false;
  }

  /**
   * Collapse all insights sections
   */
  private collapseAllInsights(): void {
    this.isTopFilesCollapsed = true;
    this.isLeastFilesCollapsed = true;
    this.isLeastFieldsCollapsed = true;
  }

  /**
   * Toggle top files insights - expands all when expanding, collapses all when collapsing
   */
  toggleTopFilesInsights(): void {
    this.isTopFilesCollapsed = !this.isTopFilesCollapsed;
    if (!this.isTopFilesCollapsed) {
      this.expandAllInsights();
    } else {
      this.collapseAllInsights();
    }
  }

  /**
   * Toggle least files insights - expands all when expanding, collapses all when collapsing
   */
  toggleLeastFilesInsights(): void {
    this.isLeastFilesCollapsed = !this.isLeastFilesCollapsed;
    if (!this.isLeastFilesCollapsed) {
      this.expandAllInsights();
    } else {
      this.collapseAllInsights();
    }
  }

  /**
   * Toggle least fields insights - expands all when expanding, collapses all when collapsing
   */
  toggleLeastFieldsInsights(): void {
    this.isLeastFieldsCollapsed = !this.isLeastFieldsCollapsed;
    if (!this.isLeastFieldsCollapsed) {
      this.expandAllInsights();
    } else {
      this.collapseAllInsights();
    }
  }

  // ==================== NAVIGATION - LEAST ACCURATE FILES ====================

  /**
   * Navigate to Truth Management page for a file from the least accurate list
   */
  navigateToLeastAccurateFile(fileName: string, model: string, fileId?: string): void {
    const queryParams: any = { documentCode: model, fileName: fileName };
    if (fileId) {
      queryParams.fileId = fileId;
    }
    this.router.navigate(['/truth-management'], { queryParams });
  }

  /**
   * Navigate to Prompt Tuning page for a field from insights
   * Field name and model are passed to the prompt tuning page
   */
  navigateToLeastAccurateField(fieldName: string, model: string): void {
    this.router.navigate(['/prompt-tuning', model], {
      queryParams: { field: fieldName, source: 'evaluation-prompt-tuning' }
    });
  }

}
